<template>
  <div>
    <div class="md-layout" v-if="roadSinister.id && !loading">
      <sinister-progress-bar
        :process="process"
        class="md-layout-item md-size-95 mx-auto"
        v-if="!extranetSinister"
      />
      <extranet-progress-bar
        :process-id="roadSinister.affected.customerProcessId"
        class="md-layout-item md-size-95 mx-auto"
        v-else
      />
    </div>
    <div class="float-button" v-if="!extranetSinister">
      <md-button
        style="margin-right: 12px"
        v-if="($isAdmin || $inGroup('GeCa')) && processIs('RS (VR)', 'SM', 'PT', 'LV', 'ES')"
        class="md-warning"
        @click="moveSinisterToCP"
      >
        <md-icon style="margin-right: 8px">done_outline</md-icon>Avanzar a CP
      </md-button>
      <md-button
        v-if="$canChange('Roads', 'sinisterroads')"
        class="md-just-icon md-simple md-info"
        @click="goToEdition"
      >
        <md-icon>edit</md-icon>
        <md-tooltip md-direction="bottom">Editar siniestro</md-tooltip>
      </md-button>
    </div>

    <tabs
      v-if="!loading && roadSinister.id"
      @clicked="tabClicked"
      :options="{ useUrlFragment: false }"
    >
      <tab :name="`${header}`">
        <general
          :roadSinister="roadSinister"
          :process="process"
          :isExtranet="extranetSinister"
          @setAffectedVisible="setAffectedVisible"
          @refresh="refresh"
        ></general>
      </tab>
      <tab name="Afectado" v-if="$canView('Sinister', 'affected') || extranetSinister">
        <affected
          :affected="roadSinister.affected"
          :roadSinister="roadSinister"
          :process="process"
          :isExtranet="extranetSinister"
          @refresh="refresh"
          @softRefresh="softRefresh"
        ></affected>
      </tab>
      <tab name="Archivos" v-if="$canView('Commons', 'file') || extranetSinister">
        <filesTab
          :sinister="roadSinister"
          :isExtranet="extranetSinister"
          @refresh="refresh"
          @softRefresh="softRefresh"
          @certificateUploaded="handleCertificateUpload"
          @requiredFilesUploaded="handleRequiredFilesUploaded"
        ></filesTab>
      </tab>
      <tab name="Bitácora" v-if="$canView('Sinister', 'binnacle') && !extranetSinister">
        <binnacle :binnacle="[]" :affectedId="roadSinister.affected.id"></binnacle>
      </tab>
      <tab
        :name="isExtranetClient ? 'Bitácora' : 'Bitácora Extranet'"
        v-if="$canView('Sinister', 'extranetbinnacle')"
      >
        <extranet-binnacle :affectedId="roadSinister.affected.id"></extranet-binnacle>
      </tab>
    </tabs>
    <loading-content
      v-else-if="loading"
      loading-icon="access_time"
      empty-icon="outlined_flag"
      empty-label="Siniestro no encontrado"
      :empty-description="`Siniestro con id &quot;${id}&quot; no encontrado.`"
      loading-label="Cargando ficha"
      loading-description="Por favor espere un momento"
      :loading="loading"
    />
    <modal width="25%" v-if="infoModal" @close="infoModal = false">
      <process-info :process="process" />
    </modal>
  </div>
</template>

<script>
import sinisterService from "@/services/sinister";
import General from "./General";
import Crew from "./Crew";
import SinisterProgressBar from "./SinisterProgressBar";
import setsPageTitleMixin from "@/mixins/setsPageTitle";
import ProcessInfo from "../../Macro/ProcessInfo";
import processCalculationsMixin from "./mixins/processCalculations";
import { lazyComponent } from "@/utils";
import { mapState } from "vuex";

const ExtranetProgressBar = () =>
  import("@/pages/CustomersExtranet/SinisterProgressBar");

const Affected = () => import("./Affected");
const FilesTab = () => import("./CasualtyFiles/index");
const Binnacle = () => import("./Binnacle");
const ExtranetBinnacle = () => import("../../extranet/Binnacle");

export default {
  name: "RoadSinisterFile",

  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    extranetSinister: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [setsPageTitleMixin, processCalculationsMixin],

  computed: {
    ...mapState("account", ["userType", "userRole"]),
    header() {
      const { extranetSinister: isExtranet } = this;
      const roadSinister = this.roadSinister;
      const folioJB = roadSinister.sinister.folioJB || "";
      const abbr =
        !isExtranet && roadSinister.id
          ? roadSinister.affected.process.abbreviation
          : "";
      const macroClass = `macro-${
        roadSinister.affected.process ? roadSinister.affected.process.color : ""
      }`;

      let folio = "";

      if (folioJB) {
        folio = `
          <span>
            Folio JB: <span class="text-warning">${folioJB}</span>
          </span>
        `;
      } else {
        folio = `
        <span class="text-danger">
          Sin Folio JB | 
        </span>
        `;
      }

      const temp = `
        <span class="mt-1">
          ${folio}
          <span class="macro-label ${macroClass} text-mid"> 
            ${abbr}
          </span>
        </span>
      `;
      return temp;
    },
    isExtranetClient() {
      return this.extranetSinister;
    },
    pageTitle() {
      const { sinisterNumber } = this.roadSinister.sinister;
      return sinisterNumber ? `Siniestro ${sinisterNumber}` : null;
    },
  },

  components: {
    ProcessInfo,
    General,
    Affected: lazyComponent(Affected),
    Crew,
    ExtranetBinnacle,
    Binnacle: lazyComponent(Binnacle),
    FilesTab: lazyComponent(FilesTab),
    SinisterProgressBar,
    ExtranetProgressBar,
  },

  data() {
    return {
      loading: null,
      crew: {},
      process: {},
      infoModal: false,
      roadSinister: {
        affected: {},
        sinister: {},
        sinisterDates: {},
        crew: null,
      },
    };
  },

  methods: {
    moveSinisterToCP() {
      this.$confirmMessage(
        {
          title:
            "¿Está seguro que desea avanzar el siniestro a Cierre de Proyecto?",
        },
        () => {
          return sinisterService
            .changeStatusToCP(this.roadSinister.affected)
            .then(() => {
              this.fetchSinister();
            })
            .catch(this.$fireErrorResponse);
        }
      );
    },
    tabClicked(selectedTab) {
      if (
        selectedTab.tab.name != "Afectado" &&
        selectedTab.tab.name != "Archivos" &&
        selectedTab.tab.name != "Bitacora"
      ) {
        this.infoModal = true;
      }
    },
    fetchSinister() {
      this.loading = true;
      const { extranetSinister } = this;
      const data = {
        id: this.id,
        isCustomer: false,
      };
      if (extranetSinister) {
        data.isCustomer = true;
      }
      return sinisterService
        .fetch(data)
        .then((roadSinister) => {
          this.roadSinister = roadSinister;
          const { proccess_info, ...process } = roadSinister.affected.process;
          const process_info = proccess_info;
          let processInfo;
          try {
            processInfo = JSON.parse(process_info);
          } catch (e) {
            processInfo = null;
          }
          this.process = {
            ...process,
            processInfo,
            activeTooltip: true,
          };
        })
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loading = false));
    },
    handleCertificateUpload() {
      this.fetchSinister();
    },
    handleRequiredFilesUploaded() {
      this.fetchSinister();
    },
    refresh() {
      this.fetchSinister();
    },
    softRefresh() {
      const { extranetSinister } = this;
      const data = {
        id: this.id,
        isCustomer: false,
      };
      if (extranetSinister) {
        data.isCustomer = true;
      }
      return sinisterService
        .fetch(data)
        .then((roadSinister) => {
          this.roadSinister = roadSinister;
          const { proccess_info, ...process } = roadSinister.affected.process;
          const process_info = proccess_info;
          let processInfo;
          try {
            processInfo = JSON.parse(process_info);
          } catch (e) {
            processInfo = null;
          }
          this.process = {
            ...process,
            processInfo,
            activeTooltip: true,
          };
        })
        .catch(this.$fireErrorResponse);
    },
    setAffectedVisible(id, setAffectedVisible) {
      sinisterService
        .affectedVisibleRepports(id, setAffectedVisible)
        .then(() => {
          this.fetchSinister();
          this.$notify({
            icon: "done",
            message: "Se actualizÓ correctamente",
            type: "success",
            verticalAlign: "botton",
            horizontalAlign: "center",
          });
        })
        .catch(this.$fireErrorResponse);
    },
    goToEdition() {
      const routeData = this.$router.resolve({
        name: "Editar Siniestro Carretero",
        params: {
          id: this.roadSinister.id,
        },
      });
      window.open(routeData.href, "_blank");
    },
  },
  created() {
    this.fetchSinister();
  },
};
</script>

<style lang="css" scoped>
.mt-1 {
  margin-top: -15px;
}
h3.md-headline {
  font-size: small;
}
.text-mid {
  font-size: medium;
}

.tabs-component {
  margin: 0 !important;
}
.float-button {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: right;
}
.tabs-component-tab.is-active {
  z-index: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/RoadSinisters/File/Index.vue